import { Component, Input } from "@angular/core";
import { ApiHttpService } from "../../../shared/services/api-http.service";
import { LoadingWrapperService } from "../../../shared/services/loading-wrapper.service";
import { FormControl, FormGroup, FormArray, FormBuilder, Validators } from "@angular/forms";
import {
  FileSaverService,
  DocumentModel
} from "../../../shared/services/file-saver.service";
import { finalize } from "rxjs/operators";
import { SeedNeedsFormModel, SeedNeedsTreatmentFormModel } from "../../services/request-form.service";
import { ExperimentDesignFormValidatorsService } from "../experiment-design-form/experiment-design-form-validators.service";

@Component({
  selector: "app-seed-needs-upload",
  templateUrl: "./seed-needs-upload.component.html",
  styleUrls: ["./seed-needs-upload.component.css"]
})

export class SeedNeedsUploadComponent {
  @Input() experimentDesignFormGroup: FormGroup;  
  @Input() crop: number;
  @Input() treatmentCount: number;

  public allContainColorMarkers = new FormControl(false);
  public errors: string[] = [];

  constructor(
    private httpService: ApiHttpService,
    private fileSaverService: FileSaverService,
    private loadingService: LoadingWrapperService,
    private formBuilder: FormBuilder,
    private formValidators: ExperimentDesignFormValidatorsService
  ) {}

  public get seedNeeds() {
    return <FormArray>this.experimentDesignFormGroup.get("seedNeeds");
  }

  upload(files) {
    this.loadingService.setMessage("Loading...");

    const criteria: ValidationCriteria = {
      cropId: this.crop
    };

    if (files.length === 0 || files.length > 1) return;

    const formData = new FormData();
    formData.append(files[0].name, files[0]);
    formData.append("validationCriteria", JSON.stringify(criteria));

    this.httpService.post<SeedNeedsFormModel[]>("seedneedsupload", formData)
      .pipe(finalize(() => this.loadingService.clearMessage()))
      .subscribe(
        entries => {
          this.experimentDesignFormGroup.setControl("seedNeeds", this.buildSeedNeedsArray(entries));
          this.updateNumberOfSeedsToStart();
          this.errors = [];
          this.allContainColorMarkers.setValue(false);
        },
        err => {
          if (err.status === 400) {
            this.errors = this.formatErrorMessages(err.error as SeedNeedsTemplateErrors);
            this.experimentDesignFormGroup.setControl("seedNeeds", this.formBuilder.array([]));
          } else {
            throw err;
          }
        });
  }

  downloadTemplate() {
    this.httpService.get<DocumentModel>("seedneedstemplate").subscribe(f => {
      this.fileSaverService.save(f);
    });
  }

  public copyDown(): void {
    const firstTreatmentArray: SeedNeedsTreatmentFormModel = (<FormArray>this.seedNeeds.at(0).get("treatments")).value;
    
    for (let i = 1; i < this.seedNeeds.length; i++) {
      const treatmentArray = <FormArray>this.seedNeeds.at(i).get("treatments");
      for (let j = 0; j < treatmentArray.length; j++) {
        treatmentArray.at(j).get("seedCount").patchValue(firstTreatmentArray[j].seedCount);
      }
    }
  }

  private buildSeedNeedsArray(entries: SeedNeedsFormModel[]): FormArray {
    const seedNeedsArray = this.formBuilder.array([]);

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];

      const treatmentArray = this.formBuilder.array([]);
      for (let t = 0; t < this.treatmentCount; t++){
        const seedCount = this.formBuilder
                               .control({value: this.treatmentCount === 1 ? entry.numberOfSeeds : 0, disabled: false }, [Validators.min(0)]);

        treatmentArray.push(this.formBuilder.group({
          treatmentNumber: this.formBuilder.control({ value: t+1, disabled: false }),
          seedCount: seedCount
        }));

        seedCount.valueChanges.subscribe(c => {
          this.updateNumberOfSeedsToStart();
        })
      }

      seedNeedsArray.push(this.formBuilder.group({
        entryNumber: this.formBuilder.control({ value: entry.entryNumber, disabled: false }),
        siid: this.formBuilder.control({ value: entry.siid, disabled: false }),
        geName: this.formBuilder.control({ value: entry.geName, disabled: false }),
        numberOfSeeds: this.formBuilder.control({ value: entry.numberOfSeeds, disabled: false }),
        treatments: treatmentArray
      }, { validator: this.formValidators.seedCountValidator() }));
    }

    return seedNeedsArray;
  }

  private formatErrorMessages(errors: SeedNeedsTemplateErrors): string[] {
    const errorMessages = [];
    errorMessages.push(...errors.fileErrors);
    errors.entryErrors.forEach(entryError => {
      errorMessages.push(
        `Line: ${entryError.lineNumber}. SIID: ${entryError.siid}. ${
          entryError.error
        }`
      );
    });

    return errorMessages;
  }

  private updateNumberOfSeedsToStart() {
    const numTreatments = (<FormArray>this.experimentDesignFormGroup.get("treatments")).length;

    for (let i = 0; i < numTreatments; i++) {
      const treatment = (<FormArray>this.experimentDesignFormGroup.get("treatments")).at(i);
      const plantingInstructions = <FormControl>treatment.get("plantingInstructions");
      const numberToStart = <FormControl>plantingInstructions.get("numberToStart");
      const numberOfSeedsPerCellControl = <FormControl>this.experimentDesignFormGroup.get('numberOfSeedsPerCell');
  
      let numberToStartTotal = 0;
      let numberOfEuids = 0;
      const seedNeeds = <FormArray>this.experimentDesignFormGroup.get("seedNeeds");
      for (let seedNeed of seedNeeds.controls) {
        const seedNeedsTreatment = (<FormArray>seedNeed.get("treatments")).at(i);
        numberToStartTotal += seedNeedsTreatment.get("seedCount").value;
        numberOfEuids += Math.ceil(seedNeedsTreatment.get("seedCount").value / numberOfSeedsPerCellControl.value);
      }
  
      numberToStart.patchValue(numberToStartTotal);
      plantingInstructions.clearValidators();
      plantingInstructions.setValidators(this.formValidators.plantingInstructionsValidator(numberOfEuids));
      plantingInstructions.updateValueAndValidity();
    }
  }
}

interface SeedNeedsTemplateErrors {
  fileErrors: string[];
  entryErrors: EntryError[];
}

interface EntryError {
  lineNumber: number;
  siid: string;
  error: string;
}

interface ValidationCriteria {
  cropId: number;
}


// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/seed-needs-upload/seed-needs-upload.component.ts